"use client";
import { Amplify, ResourcesConfig } from "aws-amplify";
import {
  AuthSession,
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { remove, set } from "es-cookie";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import config from "@ctv/core/config";
import { useLocale } from "@ctv/shared-core/src";

import { Auth, Permission, User } from "./types";

const COGNITO_COOKIE_KEY = "awscognito.is.authenticated";
const AuthContext = createContext<Auth | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export function CognitoAuthProvider(props: Props) {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User>();
  const isAuthenticated = !!user;

  const { locale } = useLocale();

  const getToken = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString();
  }, []);

  function applyUser(cognitoUser: AuthSession) {
    const user = cognitoUser.tokens?.idToken?.payload;

    if (!user) {
      return;
    }

    const {
      "https://tvlk/permissions": permissions,
      "https://tvlk/groups": _groups,
      iat,
    } = user;
    const name = user.name as string;
    const email = user.email as string;

    set(COGNITO_COOKIE_KEY, "true");

    const permissionMap = (
      JSON.parse(permissions as string) as Permission[]
    ).reduce(
      (obj, p) => {
        obj[p] = true;

        return obj;
      },
      {} as Record<Permission, true>,
    );

    setUser({
      name: name as string,
      email: email as string,
      has(...permissions: Permission[]) {
        return permissions.some((p) => permissionMap[p]);
      },
      loginTime: Number(iat) * 1000,
      isInternalUser: /@traveloka.com$/.test(email),
    });
  }

  function clearSession() {
    setUser(undefined);
    remove(COGNITO_COOKIE_KEY);
  }

  const login = useCallback(() => signInWithRedirect(), []);

  const logout = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    if (user.email.endsWith("@traveloka.com")) {
      signOut().then(() => clearSession());
      return;
    }

    clearSession();
    signOut();
  }, [isAuthenticated]);

  useEffect(() => {
    const {
      redirectSignIn: rawRedirectSignIn,
      redirectSignOut: rawRedirectSignOut,
    } = config.amplify.Auth!.Cognito.loginWith!.oauth!;

    const redirectSignIn = [rawRedirectSignIn[0] + locale + "/"];
    const redirectSignOut = [rawRedirectSignOut[0] + locale + "/"];

    Amplify.configure({
      ...config.amplify,
      Auth: {
        ...config.amplify.Auth,
        Cognito: {
          ...config.amplify.Auth!.Cognito,
          loginWith: {
            ...config.amplify.Auth!.Cognito.loginWith,
            oauth: {
              ...config.amplify.Auth!.Cognito.loginWith!.oauth!,
              redirectSignIn,
              redirectSignOut,
            },
          },
        },
      },
    } as ResourcesConfig);

    fetchAuthSession()
      .then((res) => {
        if (res) {
          applyUser(res);
        }
      })
      .catch(() => clearSession())
      .finally(() => setIsLoading(false));
  }, []);

  const value = isAuthenticated
    ? {
        getToken,
        applyUser,
        isAuthenticated,
        isLoading,
        login,
        logout,
        user,
      }
    : {
        getToken,
        applyUser,
        isAuthenticated,
        isLoading,
        login,
        logout,
        user: undefined,
      };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (value === undefined) {
    throw new Error("`AuthContext` could not find any provider");
  }

  return value;
}
